import { serializableEvent } from '../../../core/js/utils/eventUtils';
import { getQueryParamsUrl } from '../../../core/js/utils/routerUtils';
import { siteMessageSaga } from '../../../core/js/sagas/siteMessageSagas';
import { launchAsyncQuickshop } from '@product/js/utils/asyncLaunchers';
import { defaultQuickshopOptions } from '@core/js/utils/defaultQuickshopOptions';

/* eslint-disable complexity */
/**
 * Update the product tile image's src and data-alt-src attributes with
 * the selected swatch's new colorway
 * @param {object} e jQuery event
 */
export function setProductTileSwatchImage(store, e) {
    const $el = $(e.currentTarget);
    const $tileImg = $el.parents('.js-product-tile').find('.js-product-tile-img');
    const tileImgSrc = $tileImg.attr('src');
    const slugPattern = /_(\w+)_(\w+)/;
    const currentSlugMatch = tileImgSrc.match(slugPattern);
    const VALID_MATCH_LENGTH = 3;

    const selectedColorId = $el.attr('data-color-code');
    const selectedViewCode = $el.attr('data-view-code');
    const selectedAltViewCode = $el.attr('data-alt-view-code');

    // Bail when we can't extract color/view codes from image src
    if (
        !tileImgSrc ||
        !currentSlugMatch ||
        currentSlugMatch.length !== VALID_MATCH_LENGTH ||
        !selectedColorId ||
        !selectedViewCode ||
        !selectedAltViewCode
    ) {
        const error = new Error('Tile swatch failed');

        Raven.captureException(error, {
            event: serializableEvent(e)
        });
        store.runSaga(siteMessageSaga, '', {}, error);
        return;
    }

    const newTileImgSrc = tileImgSrc.replace(currentSlugMatch[0], `_${selectedColorId}_${selectedViewCode}`);

    $tileImg.attr('src', newTileImgSrc);

    const tileAltImgSrc = $tileImg.attr('data-alt-src');

    if (tileAltImgSrc) {
        const altSlugMatch = tileAltImgSrc.match(slugPattern);
        let newTileAltImgSrc = newTileImgSrc;

        if (selectedAltViewCode) {
            newTileAltImgSrc = tileAltImgSrc.replace(altSlugMatch[0], `_${selectedColorId}_${selectedAltViewCode}`);
        }

        $tileImg.attr('data-alt-src', newTileAltImgSrc);
    }

    const $videoElement = $el.parents('.js-product-tile').find('.js-product-tile-video');
    handleVideoTile($videoElement, selectedColorId);
}

export function handleVideoTile($videoElement, selectedColorId) {
    if(!$videoElement.length) {
        return;
    }

    const faceoutColorRule = $videoElement.attr('data-facout-color-rule');
    const videoSrc = $videoElement.attr('data-video-src');

    if($videoElement && faceoutColorRule && faceoutColorRule == selectedColorId && videoSrc) {
        $videoElement.addClass('js-playable-tile-video');

        if(!$videoElement.find('source').attr('src')) {
            $videoElement.find('source').attr('src', videoSrc);
            $videoElement.load();
        }
    } else if ($videoElement && faceoutColorRule && faceoutColorRule != selectedColorId) {
        $videoElement.removeClass('js-playable-tile-video');
    }
}

/* eslint-enable complexity */

/**
 * Set the product tile link to include the color code
 * when a swatch is selected.
 * @param {object} e jQuery event
 * @return {String} The new tile URL
 */
export function setProductTileSwatchUrl(e) {
    const $el = $(e.currentTarget);
    const $imageLink = $el.parents('.js-product-tile').find('.js-product-tile__image-link');
    const $quickshopLink = $el.parents('.js-product-tile').find('.js-product-tile-control-quickshop');
    const $productTitleLink = $el.parents('.js-product-tile').find('.js-product-tile-title-link');

    const colorCode = $el.attr('data-color-code');
    const oldUrl = $imageLink.attr('href');
    const newUrl = getQueryParamsUrl(oldUrl, { color: colorCode });

    $imageLink.attr('href', newUrl);
    $quickshopLink.attr('href', newUrl);
    $productTitleLink.attr('href', newUrl);

    return newUrl;
}

/**
 * Attach quickshop launch event to the product tile
 *
 * records should be a listing of product tiles usually pulled from the store
 *
     ```
     const records = _.map(store.getState().core.rrRecs.recs, (product) => {
        return product;
     });
     ```
 *
 * See also quickshopProductGenerator.js
 *
 * @param {object} store Redux store object
 * @param {object} component FerretJS component
 * @param {function} getRecords Function, with no args, that should list of products to quickshop.  Use a closure
 * @param {object} quickshopOptions optional options to pass into quickshop
 */
export function attachQuickshopLaunchEvent(store, component, getRecords, quickshopOptions) {
    // TODO: Replace use of component.templateLocation with component.scopedSelector('.js-product-tile-control-quickshop') when FPME work goes in
    $(document).on(component.scopedEvent('touchend click'), `${component.templateLocation} .js-product-tile-control-quickshop`, (event) => {
        event.preventDefault();
        event.stopPropagation();

        const productIndex = $(event.target).parents('.js-product-tile')
            .attr('data-product-index');

        launchAsyncQuickshop(
            store,
            quickshopOptions || defaultQuickshopOptions,
            event,
            getRecords(),
            productIndex
        );

        return false;
    });
}

/**
 * Update the active state of the selected color swatch
 * @param {object} e jQuery event
 */
export function setProductTileActiveSwatch(e) {
    const $el = $(e.target).closest('.js-swatch-link-category');
    const activeClass = 'o-list-swatches__a--selected';
    const isSwatchActive = $el.hasClass(activeClass);

    if (isSwatchActive) {
        return;
    }

    $el.closest('.o-list-swatches').find('.o-list-swatches__li a').removeClass(activeClass);
    $el.addClass(activeClass);
}
